import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Users, Buildings, CurrencyDollar, TrendUp, MagnifyingGlass, Gear, XCircle,
  CheckCircle, Warning, PauseCircle, XSquare, Clock, ArrowsCounterClockwise,
  Package, Sparkle, Plus,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_BADGES = {
  trial: { color: "bg-amber-100 text-amber-800 border-amber-300", icon: Clock, label: "Trial" },
  active: { color: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: CheckCircle, label: "Aktif" },
  suspended: { color: "bg-rose-100 text-rose-800 border-rose-300", icon: PauseCircle, label: "Suspended" },
  expired: { color: "bg-slate-200 text-slate-700 border-slate-300", icon: XCircle, label: "Expired" },
  cancelled: { color: "bg-slate-100 text-slate-500 border-slate-300", icon: XSquare, label: "Cancelled" },
};
const PLAN_COLORS = {
  free: "bg-slate-100 text-slate-700",
  starter: "bg-sky-100 text-sky-800",
  pro: "bg-violet-100 text-violet-800",
  business: "bg-amber-100 text-amber-800",
};
const rupiah = (n) => (Number(n || 0)).toLocaleString("id-ID", { maximumFractionDigits: 0 });

function StatCard({ label, value, sub, icon: Icon, tone = "slate", testid }) {
  const tones = {
    slate: "bg-slate-900 text-white",
    emerald: "bg-emerald-700 text-white",
    amber: "bg-amber-600 text-white",
    violet: "bg-violet-700 text-white",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
          <div className="font-serif-display text-3xl mt-1 text-slate-900">{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className={`w-10 h-10 rounded-full ${tones[tone]} flex items-center justify-center`}><Icon size={18} weight="fill" /></div>
      </div>
    </div>
  );
}

export default function SuperAdmin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ search: "", plan: "", status_filter: "" });
  const [selected, setSelected] = useState(null); // tenant detail
  const [editingPlan, setEditingPlan] = useState(null); // plan id being edited
  const [delTarget, setDelTarget] = useState(null);
  const [delConfirmText, setDelConfirmText] = useState("");
  const [delBusy, setDelBusy] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, c, t] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/config"),
        api.get("/admin/tenants", { params: filter }),
      ]);
      setStats(s.data);
      setConfig(c.data);
      setTenants(t.data.tenants || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat data admin");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (!loading) loadAll(); /* eslint-disable-next-line */ }, [filter.plan, filter.status_filter]);

  if (!user?.is_platform_admin) {
    return (
      <div className="p-10 max-w-lg mx-auto text-center">
        <XCircle size={48} className="mx-auto text-rose-500" weight="fill" />
        <h1 className="font-serif-display text-3xl mt-4 text-slate-900">Akses Ditolak</h1>
        <p className="text-sm text-slate-500 mt-2">Halaman ini hanya untuk Platform Admin.</p>
      </div>
    );
  }

  const openTenant = async (id) => {
    try {
      const r = await api.get(`/admin/tenants/${id}`);
      setSelected(r.data);
    } catch { toast.error("Gagal buka detail"); }
  };

  const changePlan = async (tid, plan) => {
    try {
      await api.put(`/admin/tenants/${tid}/plan`, { plan, reset_override: true });
      toast.success(`Plan diubah ke ${plan}`);
      await openTenant(tid);
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const changeStatus = async (tid, statusVal, extra = {}) => {
    try {
      await api.put(`/admin/tenants/${tid}/status`, { status: statusVal, ...extra });
      toast.success(`Status: ${statusVal}`);
      await openTenant(tid);
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const toggleFeature = async (tid, feature, enabled) => {
    try {
      await api.put(`/admin/tenants/${tid}/features`, { feature, enabled });
      await openTenant(tid);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const clearOverrides = async (tid) => {
    if (!window.confirm("Reset semua override fitur ke default plan?")) return;
    try {
      await api.delete(`/admin/tenants/${tid}/features/overrides`);
      toast.success("Override direset");
      await openTenant(tid);
      await loadAll();
    } catch { toast.error("Gagal reset"); }
  };

  const deleteTenant = async () => {
    if (!delTarget || delConfirmText !== delTarget.name) return;
    setDelBusy(true);
    try {
      const r = await api.delete(`/admin/tenants/${delTarget.id}`);
      toast.success(`Workspace "${r.data.name}" dihapus (${r.data.collections_affected} koleksi dibersihkan)`);
      setSelected(null);
      setDelTarget(null);
      setDelConfirmText("");
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
    finally { setDelBusy(false); }
  };

  const savePlanConfig = async (planId, updates) => {
    try {
      await api.put(`/admin/config/plans/${planId}`, updates);
      toast.success(`Plan ${planId} tersimpan`);
      setEditingPlan(null);
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl" data-testid="superadmin-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-600">Platform Console</div>
          <h1 className="font-serif-display text-4xl font-medium text-slate-900">Super Admin</h1>
          <p className="text-sm text-slate-500 mt-1">Kelola pelanggan SaaS, paket berlangganan, dan fitur.</p>
        </div>
        <Button onClick={loadAll} variant="outline" size="sm" data-testid="sa-refresh">
          <ArrowsCounterClockwise size={13} className="mr-1" /> Refresh
        </Button>
      </div>

      <div className="flex gap-1 mt-6 border-b border-slate-200">
        {[
          { id: "dashboard", label: "Dashboard" },
          { id: "customers", label: "Pelanggan" },
          { id: "plans", label: "Paket & Harga" },
          { id: "payments", label: "Integrasi Pembayaran" },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t.id ? "border-amber-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}
                  data-testid={`sa-tab-${t.id}`}>{t.label}</button>
        ))}
      </div>

      {tab === "dashboard" && stats && (
        <div className="mt-6 space-y-6" data-testid="sa-dashboard">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Total Customer" value={stats.total_customers} icon={Buildings} tone="slate" testid="sa-stat-total" />
            <StatCard label="MRR Estimasi" value={`Rp ${rupiah(stats.mrr_estimate)}`} sub="per bulan (active + trial, non-free)" icon={CurrencyDollar} tone="emerald" testid="sa-stat-mrr" />
            <StatCard label="Baru Minggu Ini" value={`+${stats.new_this_week}`} icon={TrendUp} tone="amber" testid="sa-stat-new" />
            <StatCard label="Aktivitas 30 hari" value={stats.top_by_activity.reduce((a, t) => a + t.activity_30d, 0)} sub="total jurnal (top 10)" icon={Sparkle} tone="violet" testid="sa-stat-activity" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-md p-5">
              <h3 className="font-serif-display text-xl text-slate-900 mb-3">Distribusi Paket</h3>
              <div className="space-y-2" data-testid="sa-plan-dist">
                {Object.entries(stats.per_plan).map(([plan, count]) => (
                  <div key={plan} className="flex items-center justify-between text-sm">
                    <span className={`text-xs px-2 py-0.5 rounded ${PLAN_COLORS[plan]}`}>{plan}</span>
                    <div className="flex-1 mx-3 h-2 bg-slate-100 rounded overflow-hidden">
                      <div className="h-full bg-amber-500" style={{ width: `${Math.min(100, (count / Math.max(1, stats.total_customers)) * 100)}%` }} />
                    </div>
                    <span className="font-medium text-slate-700 tabular-nums w-10 text-right">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-md p-5">
              <h3 className="font-serif-display text-xl text-slate-900 mb-3">Distribusi Status</h3>
              <div className="space-y-2" data-testid="sa-status-dist">
                {Object.entries(stats.per_status).map(([st, count]) => {
                  const meta = STATUS_BADGES[st];
                  return (
                    <div key={st} className="flex items-center justify-between text-sm">
                      <span className={`text-xs px-2 py-0.5 rounded border ${meta.color}`}>{meta.label}</span>
                      <div className="flex-1 mx-3 h-2 bg-slate-100 rounded overflow-hidden">
                        <div className="h-full bg-slate-700" style={{ width: `${Math.min(100, (count / Math.max(1, stats.total_customers)) * 100)}%` }} />
                      </div>
                      <span className="font-medium text-slate-700 tabular-nums w-10 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-5">
            <h3 className="font-serif-display text-xl text-slate-900 mb-3">Top 10 Aktivitas 30 Hari</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="sa-top-activity">
                <thead className="bg-slate-50 text-slate-500 uppercase tracking-widest">
                  <tr><th className="text-left p-2">#</th><th className="text-left p-2">Customer</th><th className="text-left p-2">Plan</th><th className="text-left p-2">Status</th><th className="text-right p-2">Users</th><th className="text-right p-2">Jurnal (30h)</th></tr>
                </thead>
                <tbody>
                  {stats.top_by_activity.map((t, i) => (
                    <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => { setTab("customers"); openTenant(t.id); }} data-testid={`sa-top-${t.id}`}>
                      <td className="p-2 text-slate-400">{i + 1}</td>
                      <td className="p-2 font-medium">{t.name}</td>
                      <td className="p-2"><span className={`text-xs px-2 py-0.5 rounded ${PLAN_COLORS[t.plan]}`}>{t.plan}</span></td>
                      <td className="p-2"><span className={`text-xs px-2 py-0.5 rounded border ${STATUS_BADGES[t.status]?.color || ""}`}>{STATUS_BADGES[t.status]?.label || t.status}</span></td>
                      <td className="p-2 text-right tabular-nums">{t.user_count}</td>
                      <td className="p-2 text-right tabular-nums font-medium text-amber-700">{t.activity_30d}</td>
                    </tr>
                  ))}
                  {stats.top_by_activity.length === 0 && <tr><td colSpan={6} className="p-4 text-center text-slate-400 italic">Belum ada aktivitas 30 hari terakhir.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === "customers" && (
        <div className="mt-6" data-testid="sa-customers">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4">
            <div className="md:col-span-2 relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Cari nama customer..." value={filter.search} onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                     onKeyDown={(e) => e.key === "Enter" && loadAll()} className="pl-9" data-testid="sa-search" />
            </div>
            <select value={filter.plan} onChange={(e) => setFilter({ ...filter, plan: e.target.value })} className="border border-slate-200 rounded px-3 py-2 text-sm" data-testid="sa-filter-plan">
              <option value="">Semua Plan</option>
              <option value="free">Free</option><option value="starter">Starter</option>
              <option value="pro">Pro</option><option value="business">Business</option>
            </select>
            <select value={filter.status_filter} onChange={(e) => setFilter({ ...filter, status_filter: e.target.value })} className="border border-slate-200 rounded px-3 py-2 text-sm" data-testid="sa-filter-status">
              <option value="">Semua Status</option>
              {Object.entries(STATUS_BADGES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>

          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 uppercase tracking-widest">
                  <tr>
                    <th className="text-left p-2">Customer</th>
                    <th className="text-left p-2">Owner</th>
                    <th className="text-left p-2">Plan</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-right p-2">Users</th>
                    <th className="text-right p-2">Jurnal 30h</th>
                    <th className="text-right p-2">Override</th>
                    <th className="text-right p-2"></th>
                  </tr>
                </thead>
                <tbody data-testid="sa-tenants-tbody">
                  {tenants.map((t) => (
                    <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`sa-tenant-${t.id}`}>
                      <td className="p-2">
                        <div className="font-medium">{t.name}</div>
                        <div className="text-[10px] text-slate-400 font-mono-plex">{t.id.slice(0, 8)}...</div>
                      </td>
                      <td className="p-2">
                        <div className="text-slate-700">{t.owner_name || "-"}</div>
                        <div className="text-[10px] text-slate-400">{t.owner_email}</div>
                      </td>
                      <td className="p-2"><span className={`text-xs px-2 py-0.5 rounded ${PLAN_COLORS[t.plan]}`}>{t.plan}</span></td>
                      <td className="p-2"><span className={`text-xs px-2 py-0.5 rounded border ${STATUS_BADGES[t.status]?.color || ""}`}>{STATUS_BADGES[t.status]?.label || t.status}</span></td>
                      <td className="p-2 text-right tabular-nums">{t.user_count}</td>
                      <td className="p-2 text-right tabular-nums font-medium text-amber-700">{t.activity_30d}</td>
                      <td className="p-2 text-right tabular-nums text-slate-500">{t.features_override_count > 0 ? `${t.features_override_count} 🔧` : "-"}</td>
                      <td className="p-2 text-right space-x-1 whitespace-nowrap">
                        <Button size="sm" variant="outline" onClick={() => openTenant(t.id)} data-testid={`sa-open-${t.id}`}>Kelola</Button>
                        <Button size="sm" variant="outline" onClick={() => { setDelTarget(t); setDelConfirmText(""); }} data-testid={`sa-del-${t.id}`} className="border-rose-300 text-rose-700 hover:bg-rose-50">Hapus</Button>
                      </td>
                    </tr>
                  ))}
                  {tenants.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-400 italic">Belum ada customer sesuai filter.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === "plans" && config && (
        <div className="mt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="sa-plans">
            {Object.entries(config.plans).map(([id, plan]) => (
              <PlanCard key={id} id={id} plan={plan} featureLabels={config.feature_labels}
                        editing={editingPlan === id} onEdit={() => setEditingPlan(id)}
                        onCancel={() => setEditingPlan(null)} onSave={(u) => savePlanConfig(id, u)} />
            ))}
          </div>
          <BillingPeriodsEditor />
        </div>
      )}

      {tab === "payments" && <PaymentGatewaysTab />}

      {selected && (
        <TenantDrawer tenant={selected} config={config} onClose={() => setSelected(null)}
                      onChangePlan={changePlan} onChangeStatus={changeStatus}
                      onToggleFeature={toggleFeature} onClearOverrides={clearOverrides} />
      )}

      <Dialog open={!!delTarget} onOpenChange={(o) => { if (!o && !delBusy) { setDelTarget(null); setDelConfirmText(""); } }}>
        <DialogContent className="max-w-md" data-testid="sa-del-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Warning size={18} weight="fill" /> Hapus Workspace
            </DialogTitle>
          </DialogHeader>
          {delTarget && (
            <div className="space-y-3 text-sm">
              <div className="rounded border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800 leading-relaxed">
                <strong>Peringatan:</strong> tindakan ini akan menghapus <strong>SELURUH data</strong> workspace
                <span className="font-semibold"> "{delTarget.name}"</span> secara <strong>permanen</strong> dan
                tidak dapat dikembalikan — termasuk master, jurnal, faktur, proyek, lampiran, dan log.
              </div>
              <div>
                <Label className="text-xs text-slate-600">Ketik ulang <code className="font-mono-plex bg-slate-100 px-1 rounded">{delTarget.name}</code> untuk konfirmasi</Label>
                <Input
                  value={delConfirmText}
                  onChange={(e) => setDelConfirmText(e.target.value)}
                  autoFocus
                  placeholder={delTarget.name}
                  className={delConfirmText && delConfirmText !== delTarget.name ? "border-rose-300" : ""}
                  data-testid="sa-del-confirm-input"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setDelTarget(null); setDelConfirmText(""); }} disabled={delBusy} data-testid="sa-del-cancel">Batal</Button>
            <Button
              onClick={deleteTenant}
              disabled={delBusy || !delTarget || delConfirmText !== delTarget.name}
              className="bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-40"
              data-testid="sa-del-confirm"
            >
              {delBusy ? "Menghapus…" : "Hapus Permanen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

function BillingPeriodsEditor() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/config/billing-periods"); setRows(r.data.billing_periods || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const add = () => setRows([...rows, { months: Math.max(...rows.map(r => r.months), 0) + 1, label: "Baru", discount_pct: 0 }]);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/config/billing-periods", rows);
      toast.success("Jangka waktu tersimpan");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
    finally { setBusy(false); }
  };
  if (loading) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="sa-billing-periods">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-serif-display text-2xl text-slate-900">Jangka Waktu Berlangganan</h3>
          <p className="text-xs text-slate-500 mt-0.5">Preset durasi untuk Billing & Landing. Diskon berlaku saat pelanggan memilih jangka waktu yang persis cocok.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={add} variant="outline" size="sm" data-testid="sa-bp-add"><Plus size={13} className="mr-1" />Tambah</Button>
          <Button onClick={save} disabled={busy || rows.length === 0} size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="sa-bp-save">Simpan</Button>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
          <tr><th className="text-left px-3 py-2">Jumlah Bulan</th><th className="text-left px-3 py-2">Label</th><th className="text-left px-3 py-2">Diskon (%)</th><th></th></tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100" data-testid={`sa-bp-row-${i}`}>
              <td className="px-3 py-2"><Input type="number" min="1" max="120" value={r.months} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, months: Number(e.target.value) || 1 } : x))} data-testid={`sa-bp-months-${i}`} /></td>
              <td className="px-3 py-2"><Input value={r.label} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} placeholder="mis. 6 Bulan / Semester" data-testid={`sa-bp-label-${i}`} /></td>
              <td className="px-3 py-2"><Input type="number" min="0" max="100" step="0.5" value={r.discount_pct} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, discount_pct: Number(e.target.value) || 0 } : x))} data-testid={`sa-bp-discount-${i}`} /></td>
              <td className="px-3 py-2 text-right"><Button variant="outline" size="sm" onClick={() => setRows(rows.filter((_, j) => j !== i))} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`sa-bp-del-${i}`}><XCircle size={14} /></Button></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={4} className="text-center text-slate-400 py-6">Belum ada preset. Klik "Tambah".</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function PlanCard({ id, plan, featureLabels, editing, onEdit, onCancel, onSave }) {
  const [draft, setDraft] = useState(plan);
  useEffect(() => { setDraft(plan); }, [plan, editing]);

  const enabledCount = Object.values(draft.features || {}).filter(Boolean).length;

  if (!editing) {
    return (
      <div className={`border rounded-md p-5 flex flex-col ${id === "pro" ? "border-amber-400 bg-amber-50/30" : "border-slate-200 bg-white"}`} data-testid={`sa-plan-${id}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className={`text-xs px-2 py-0.5 rounded inline-block ${PLAN_COLORS[id]}`}>{id}</div>
            <h3 className="font-serif-display text-2xl mt-1 text-slate-900">{plan.name}</h3>
          </div>
          {id === "pro" && <span className="text-[10px] bg-amber-600 text-white px-2 py-1 rounded">Populer</span>}
        </div>
        <div className="mt-3">
          {plan.original_price > plan.price && (
            <div className="text-xs text-slate-400 line-through">Rp {rupiah(plan.original_price)}</div>
          )}
          <div className="font-serif-display text-3xl text-slate-900 tabular-nums">
            Rp {rupiah(plan.price)}<span className="text-xs text-slate-500 ml-1">/bulan</span>
          </div>
          {plan.discount_pct > 0 && <div className="text-xs text-emerald-700 font-medium mt-0.5">Hemat {plan.discount_pct}%</div>}
        </div>
        <p className="text-xs text-slate-500 mt-3 flex-1">{plan.description}</p>
        <div className="mt-4 text-xs">
          <div className="font-medium text-slate-700 mb-1">Fitur ({enabledCount} aktif):</div>
          <div className="space-y-0.5 max-h-40 overflow-auto">
            {Object.entries(plan.features || {}).map(([f, on]) => (
              <div key={f} className={`flex items-center gap-1.5 ${on ? "text-slate-700" : "text-slate-300 line-through"}`}>
                {on ? <CheckCircle size={11} className="text-emerald-600" weight="fill" /> : <XCircle size={11} />}
                <span>{featureLabels[f] || f}</span>
              </div>
            ))}
          </div>
        </div>
        <Button onClick={onEdit} className="mt-4 bg-slate-900 hover:bg-slate-800 text-white" size="sm" data-testid={`sa-edit-plan-${id}`}>
          <Gear size={12} className="mr-1" /> Edit Plan
        </Button>
      </div>
    );
  }

  return (
    <div className="border-2 border-amber-500 rounded-md p-5 bg-white col-span-full" data-testid={`sa-plan-${id}-editing`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-serif-display text-xl text-slate-900">Edit {plan.name}</h3>
        <div className="flex gap-2">
          <Button onClick={onCancel} variant="outline" size="sm">Batal</Button>
          <Button onClick={() => onSave(draft)} className="bg-emerald-700 hover:bg-emerald-800 text-white" size="sm" data-testid={`sa-save-plan-${id}`}>Simpan</Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div><Label className="text-xs">Nama</Label><Input value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid={`sa-plan-${id}-name`} /></div>
        <div><Label className="text-xs">Harga (Rp)</Label><MoneyInput value={draft.price || 0} onChange={(e) => setDraft({ ...draft, price: Number(e.target.value) })} data-testid={`sa-plan-${id}-price`} /></div>
        <div><Label className="text-xs">Harga Coret (Rp)</Label><MoneyInput value={draft.original_price || 0} onChange={(e) => setDraft({ ...draft, original_price: Number(e.target.value) })} data-testid={`sa-plan-${id}-orig-price`} /></div>
        <div><Label className="text-xs">Diskon (%)</Label><Input type="number" value={draft.discount_pct || 0} onChange={(e) => setDraft({ ...draft, discount_pct: Number(e.target.value) })} /></div>
      </div>
      <div><Label className="text-xs">Deskripsi</Label><Input value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></div>
      <div className="mt-4">
        <Label className="text-xs">Fitur baku plan ini:</Label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-1 mt-2">
          {Object.keys(featureLabels).map((f) => {
            const on = !!(draft.features || {})[f];
            return (
              <button key={f} onClick={() => setDraft({ ...draft, features: { ...(draft.features || {}), [f]: !on } })}
                      className={`text-xs px-3 py-2 border rounded text-left flex items-center gap-2 ${on ? "border-emerald-500 bg-emerald-50 text-emerald-900" : "border-slate-200 bg-slate-50 text-slate-500"}`}
                      data-testid={`sa-plan-${id}-feat-${f}`}>
                {on ? <CheckCircle size={12} weight="fill" className="text-emerald-600" /> : <XCircle size={12} />}
                {featureLabels[f]}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function TenantDrawer({ tenant, config, onClose, onChangePlan, onChangeStatus, onToggleFeature, onClearOverrides }) {
  const [statusMenu, setStatusMenu] = useState(false);
  const [trialDays, setTrialDays] = useState(14);

  const overrideKeys = Object.keys(tenant.features_override || {});
  const featureLabels = config?.feature_labels || {};

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose} data-testid="sa-drawer">
      <div className="w-full max-w-2xl bg-white shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="sa-drawer-panel">
        <div className="p-6 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Customer Detail</div>
              <h2 className="font-serif-display text-2xl text-slate-900">{tenant.name}</h2>
              <div className="text-[11px] text-slate-400 font-mono-plex mt-1">{tenant.id}</div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><XCircle size={22} /></button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Plan */}
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Paket Berlangganan</div>
            <div className="flex flex-wrap gap-2">
              {["free", "starter", "pro", "business"].map((p) => (
                <button key={p} onClick={() => onChangePlan(tenant.id, p)}
                        className={`px-4 py-2 border rounded-md text-sm font-medium ${tenant.plan === p ? "border-amber-500 bg-amber-50 text-amber-900" : "border-slate-200 hover:bg-slate-50"}`}
                        data-testid={`sa-change-plan-${p}`}>
                  {p === tenant.plan && <CheckCircle size={12} className="inline mr-1 text-emerald-600" weight="fill" />}
                  {p}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-2">Mengubah plan otomatis reset override fitur ke default plan tersebut.</p>
          </div>

          {/* Status */}
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Status</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(STATUS_BADGES).map(([st, meta]) => (
                <button key={st} onClick={() => { if (st === "trial") setStatusMenu("trial"); else onChangeStatus(tenant.id, st); }}
                        className={`px-3 py-1.5 border rounded-md text-xs ${tenant.status === st ? `${meta.color} border-2` : "border-slate-200 hover:bg-slate-50"}`}
                        data-testid={`sa-change-status-${st}`}>
                  {tenant.status === st && "✓ "}{meta.label}
                </button>
              ))}
            </div>
            {statusMenu === "trial" && (
              <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-3 flex items-center gap-2">
                <Label className="text-xs">Trial berakhir:</Label>
                <Input type="number" value={trialDays} onChange={(e) => setTrialDays(Number(e.target.value))} className="w-20" />
                <span className="text-xs text-slate-500">hari dari sekarang</span>
                <Button size="sm" onClick={() => { onChangeStatus(tenant.id, "trial", { trial_days: trialDays }); setStatusMenu(false); }} className="bg-amber-600 text-white ml-auto" data-testid="sa-confirm-trial">Simpan Trial</Button>
              </div>
            )}
            <div className="mt-2 text-[11px] text-slate-500">
              {tenant.trial_ends_at && <>Trial berakhir: <span className="font-mono-plex">{tenant.trial_ends_at}</span></>}
              {tenant.subscription_ends_at && <span className="ml-4">Langganan berakhir: <span className="font-mono-plex">{tenant.subscription_ends_at}</span></span>}
            </div>
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-slate-50 rounded p-3"><div className="text-[10px] uppercase text-slate-500">Users</div><div className="text-xl font-serif-display">{tenant.users.length}</div></div>
            <div className="bg-slate-50 rounded p-3"><div className="text-[10px] uppercase text-slate-500">Jurnal 30h</div><div className="text-xl font-serif-display text-amber-700">{tenant.activity_30d}</div></div>
            <div className="bg-slate-50 rounded p-3"><div className="text-[10px] uppercase text-slate-500">Total Jurnal</div><div className="text-xl font-serif-display">{tenant.doc_counts.journal_entries}</div></div>
          </div>

          {/* Features */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-widest text-slate-500">Fitur Efektif ({Object.values(tenant.features_effective).filter(Boolean).length} aktif)</div>
              {overrideKeys.length > 0 && (
                <Button size="sm" variant="outline" onClick={() => onClearOverrides(tenant.id)} className="text-xs text-rose-600 hover:bg-rose-50" data-testid="sa-clear-overrides">
                  Reset {overrideKeys.length} override
                </Button>
              )}
            </div>
            <div className="space-y-1">
              {Object.entries(tenant.features_effective).map(([f, on]) => {
                const isOverride = f in (tenant.features_override || {});
                const planDefault = tenant.features_plan_defaults[f] || false;
                return (
                  <div key={f} className={`flex items-center justify-between px-3 py-2 rounded border ${isOverride ? "border-amber-300 bg-amber-50" : "border-slate-100"}`} data-testid={`sa-feat-${f}`}>
                    <div className="flex items-center gap-2">
                      {on ? <CheckCircle size={14} className="text-emerald-600" weight="fill" /> : <XCircle size={14} className="text-slate-300" />}
                      <div>
                        <div className={`text-sm ${on ? "text-slate-900" : "text-slate-400"}`}>{featureLabels[f] || f}</div>
                        <div className="text-[10px] text-slate-400">
                          Default plan: {planDefault ? "ON" : "OFF"}
                          {isOverride && <span className="ml-2 text-amber-700 font-medium">🔧 Override</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => onToggleFeature(tenant.id, f, true)} className={`px-2 py-1 text-[10px] rounded ${on ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-emerald-50"}`} data-testid={`sa-feat-${f}-on`}>ON</button>
                      <button onClick={() => onToggleFeature(tenant.id, f, false)} className={`px-2 py-1 text-[10px] rounded ${!on ? "bg-rose-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-rose-50"}`} data-testid={`sa-feat-${f}-off`}>OFF</button>
                      {isOverride && <button onClick={() => onToggleFeature(tenant.id, f, null)} className="px-2 py-1 text-[10px] rounded bg-slate-100 text-slate-500 hover:bg-amber-50" title="Reset ke default plan" data-testid={`sa-feat-${f}-reset`}>↺</button>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Users */}
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Team ({tenant.users.length} user)</div>
            <div className="space-y-1">
              {tenant.users.map((u) => (
                <div key={u.id} className="flex items-center justify-between text-xs px-3 py-2 border border-slate-100 rounded">
                  <div>
                    <div className="font-medium text-slate-800">{u.full_name}</div>
                    <div className="text-[10px] text-slate-400">{u.email}</div>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-600">{u.role}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PaymentGatewaysTab() {
  const [pg, setPg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    active_provider: "none",
    xendit: { secret_key: "", webhook_token: "", mode: "simulation" },
    doit: { api_key: "", webhook_secret: "", mode: "simulation" },
  });
  const baseUrl = window.location.origin;

  const load = () => {
    api.get("/admin/payment-gateways").then((r) => {
      setPg(r.data);
      setForm({
        active_provider: r.data.active_provider || "none",
        xendit: { secret_key: "", webhook_token: "", mode: r.data.xendit?.mode || "simulation" },
        doit: { api_key: "", webhook_secret: "", mode: r.data.doit?.mode || "simulation" },
      });
    }).catch(() => toast.error("Gagal memuat konfigurasi pembayaran"));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/payment-gateways", form);
      toast.success("Konfigurasi pembayaran disimpan");
      setPg({ active_provider: r.data.active_provider, xendit: r.data.xendit, doit: r.data.doit });
      setForm((f) => ({
        ...f,
        xendit: { ...f.xendit, secret_key: "", webhook_token: "" },
        doit: { ...f.doit, api_key: "", webhook_secret: "" },
      }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  const SetBadge = ({ set, prefix }) => set
    ? <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded ml-2">tersimpan ({prefix}…)</span>
    : <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded ml-2">belum diset</span>;

  if (!pg) return <div className="mt-6 text-sm text-slate-500">Memuat…</div>;

  return (
    <div className="mt-6 space-y-6 max-w-3xl" data-testid="sa-payments-tab">
      {/* Provider aktif */}
      <div className="bg-white border border-slate-200 rounded-md p-5">
        <h3 className="font-serif-display text-xl font-medium mb-1">Gateway Aktif untuk Langganan</h3>
        <p className="text-xs text-slate-500 mb-4">
          Pelanggan membayar langganan bulanan melalui gateway ini. Kredensial disimpan di level platform — tenant tidak perlu konfigurasi apapun.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { id: "xendit", label: "Xendit", desc: "Invoice hosted — QRIS, VA, e-wallet, kartu kredit" },
            { id: "doit", label: "doit.id", desc: "QRIS / VA / transfer — gateway lokal" },
            { id: "none", label: "Nonaktif", desc: "Mode simulasi (tanpa pembayaran riil)" },
          ].map((p) => (
            <label key={p.id} className={`border rounded-md p-4 cursor-pointer transition ${form.active_provider === p.id ? "border-amber-500 bg-amber-50/40 ring-1 ring-amber-300" : "border-slate-200 hover:border-slate-300"}`}
                   data-testid={`sa-provider-${p.id}`}>
              <div className="flex items-center gap-2">
                <input type="radio" name="gw-provider" checked={form.active_provider === p.id}
                       onChange={() => setForm({ ...form, active_provider: p.id })} />
                <span className="font-medium text-sm">{p.label}</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">{p.desc}</div>
            </label>
          ))}
        </div>
      </div>

      {/* Xendit */}
      <div className={`bg-white border rounded-md p-5 ${form.active_provider === "xendit" ? "border-amber-300" : "border-slate-200"}`} data-testid="sa-xendit-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Xendit</h3>
          <div className="text-xs flex items-center">
            Secret Key <SetBadge set={pg.xendit?.secret_key_set} prefix={pg.xendit?.secret_key_prefix} />
            <span className="mx-2 text-slate-300">|</span>
            Webhook Token <SetBadge set={pg.xendit?.webhook_token_set} prefix={pg.xendit?.webhook_token_prefix} />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Secret Key <span className="text-slate-400">(kosongkan bila tidak ganti)</span></Label>
            <Input type="password" placeholder={pg.xendit?.secret_key_set ? "••••••••" : "xnd_development_... / xnd_production_..."}
                   value={form.xendit.secret_key}
                   onChange={(e) => setForm({ ...form, xendit: { ...form.xendit, secret_key: e.target.value } })}
                   data-testid="sa-xendit-secret" />
          </div>
          <div>
            <Label className="text-xs">Webhook Verification Token</Label>
            <Input type="password" placeholder={pg.xendit?.webhook_token_set ? "••••••••" : "Dari Settings → Webhooks"}
                   value={form.xendit.webhook_token}
                   onChange={(e) => setForm({ ...form, xendit: { ...form.xendit, webhook_token: e.target.value } })}
                   data-testid="sa-xendit-token" />
          </div>
          <div>
            <Label className="text-xs">Mode</Label>
            <select className="w-full border border-slate-300 rounded px-2.5 py-2 text-sm"
                    value={form.xendit.mode}
                    onChange={(e) => setForm({ ...form, xendit: { ...form.xendit, mode: e.target.value } })}
                    data-testid="sa-xendit-mode">
              <option value="simulation">Simulasi</option>
              <option value="test">Test (Xendit Test Mode)</option>
              <option value="live">Live (Produksi)</option>
            </select>
          </div>
        </div>
        <div className="mt-3 text-[11px] text-slate-500 bg-slate-50 border border-slate-100 rounded p-2">
          Daftarkan webhook ini di Xendit Dashboard → Settings → Webhooks:{" "}
          <code className="font-mono-plex bg-white px-1 py-0.5 rounded border">{baseUrl}/api/xendit/webhook</code>
          <div className="mt-1">Dapatkan key di dashboard.xendit.co → Settings → API Keys → Generate Secret Key.</div>
        </div>
      </div>

      {/* doit.id */}
      <div className={`bg-white border rounded-md p-5 ${form.active_provider === "doit" ? "border-amber-300" : "border-slate-200"}`} data-testid="sa-doit-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">doit.id</h3>
          <div className="text-xs flex items-center">
            API Key <SetBadge set={pg.doit?.api_key_set} prefix={pg.doit?.api_key_prefix} />
            <span className="mx-2 text-slate-300">|</span>
            Webhook Secret <SetBadge set={pg.doit?.webhook_secret_set} prefix={pg.doit?.webhook_secret_prefix} />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">API Key <span className="text-slate-400">(kosongkan bila tidak ganti)</span></Label>
            <Input type="password" placeholder={pg.doit?.api_key_set ? "••••••••" : "pb_test_... / pb_live_..."}
                   value={form.doit.api_key}
                   onChange={(e) => setForm({ ...form, doit: { ...form.doit, api_key: e.target.value } })}
                   data-testid="sa-doit-key" />
          </div>
          <div>
            <Label className="text-xs">Webhook Signing Secret</Label>
            <Input type="password" placeholder={pg.doit?.webhook_secret_set ? "••••••••" : "Dari Portal → Developer"}
                   value={form.doit.webhook_secret}
                   onChange={(e) => setForm({ ...form, doit: { ...form.doit, webhook_secret: e.target.value } })}
                   data-testid="sa-doit-secret" />
          </div>
          <div>
            <Label className="text-xs">Mode</Label>
            <select className="w-full border border-slate-300 rounded px-2.5 py-2 text-sm"
                    value={form.doit.mode}
                    onChange={(e) => setForm({ ...form, doit: { ...form.doit, mode: e.target.value } })}
                    data-testid="sa-doit-mode">
              <option value="simulation">Simulasi</option>
              <option value="sandbox">Sandbox (pb_test_)</option>
              <option value="live">Live (pb_live_)</option>
            </select>
          </div>
        </div>
        <div className="mt-3 text-[11px] text-slate-500 bg-slate-50 border border-slate-100 rounded p-2">
          Daftarkan webhook ini di Portal doit.id → Developer:{" "}
          <code className="font-mono-plex bg-white px-1 py-0.5 rounded border">{baseUrl}/api/doit/webhook</code>
          <div className="mt-1">Dapatkan key di pay.doit.id/portal → Developer → Create Application.</div>
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="sa-payments-save">
        {saving ? "Menyimpan…" : "Simpan Konfigurasi Pembayaran"}
      </Button>
    </div>
  );
}
